import {
  __,
  allPass,
  any,
  anyPass,
  complement,
  includes,
  converge,
  curry,
  filter,
  identity,
  isEmpty,
  isNil,
  lensProp,
  map,
  mergeAll,
  pipe,
  prop,
  propEq,
  set,
  unapply,
  view,
} from "ramda";
import { validateTerms } from "./termsValidation";
import { getOptIns as getApplicationOptIns } from "application/applicationReducer";
import * as termsActions from "./termsActions";
import * as applicationActions from "application/applicationActions";
import * as submissionActions from "submission/submissionActions";
import {
  completeStep,
  getField,
  initialStepState,
  setCompleted,
  setErrors,
  setField,
  setFields,
  setLoadingLocalStorage,
} from "common/stepReducerCommon";
import {
  acceptOptIn,
  declineOptIn,
  getAcceptedOptIns,
  getDeclinedOptIns,
  getOptIns,
  initialOptInsState,
  setAcceptedOptIns,
  setDeclinedOptIns,
  setOptInTermsDialogLoading,
  setOptIns,
  undoOptIn,
  viewOptInTerms,
} from "common/optInReducerCommon";
import { getWorkType } from "../submission/submissionReducer";
import * as steps from "../common/steps";

const initialTermsStepState = {
  iframeLoading: true,
  fields: {
    termsAccepted: false,
  },
};

const initialState = mergeAll([
  initialStepState,
  initialOptInsState,
  initialTermsStepState,
]);

export const iframeLoadingLens = lensProp("iframeLoading");
export const setIframeLoading = set(iframeLoadingLens);
export const getIframeLoading = view(iframeLoadingLens);

export const getTermsOptIns = curry(
  (detailsState, basicDetailsState, submissionState, state) => {
    const optIns = getOptIns(state);
    const categoryIds = getCategoryIds(detailsState);
    const regionCode = getField(["region", "code"], basicDetailsState);
    const contentType = getWorkType(submissionState).contentTypeName;

    return filter(isTriggered(categoryIds, regionCode, contentType), optIns);
  }
);
const getOptInTriggers = curry((triggerField, optIn) =>
  filter(propEq("triggerField", triggerField), optIn.optInTriggers)
);

const getOptInTriggerValues = curry((triggerField, optIn) =>
  pipe(getOptInTriggers(triggerField), map(prop("triggerValue")))(optIn)
);

const isCategoryTriggered = curry((categoryIds, optIn) =>
  pipe(
    getOptInTriggerValues("CATEGORY"),
    anyPass([isEmpty, any(includes(__, categoryIds))])
  )(optIn)
);

const isRegionTriggered = curry((regionCode, optIn) =>
  pipe(
    getOptInTriggerValues("STATE"),
    anyPass([isEmpty, includes(regionCode)])
  )(optIn)
);

const isContentTypeTriggered = curry((contentType, optIn) =>
  pipe(
    getOptInTriggerValues("CONTENT_TYPE"),
    anyPass([isEmpty, includes(contentType)])
  )(optIn)
);

const getCategoryIds = pipe(
  converge(unapply(identity), [
    getField(["primaryCategory"]),
    getField(["secondaryCategory"]),
    getField(["tertiaryCategory"]),
  ]),
  filter(allPass([complement(isEmpty), complement(isNil)])),
  map(prop("categoryId"))
);

const isTriggered = curry((categoryIds, regionCode, contentType, optIn) =>
  allPass([
    isCategoryTriggered(categoryIds),
    isRegionTriggered(regionCode),
    isContentTypeTriggered(contentType),
  ])(optIn)
);

const localStorageLoaded = (data, state) =>
  pipe(
    setLoadingLocalStorage(false),
    setCompleted(isEmpty(data) ? initialState.completed : data.completed),
    setFields(isEmpty(data) ? initialState.fields : data.fields),
    setAcceptedOptIns(
      isEmpty(data) ? initialState.acceptedOptIns : data.acceptedOptIns
    ),
    setDeclinedOptIns(
      isEmpty(data) ? initialState.declinedOptIns : data.declinedOptIns
    )
  )(state);

const addAnotherWork = pipe(
  setCompleted(false),
  setField(["termsAccepted"], false),
  setAcceptedOptIns(initialState.acceptedOptIns),
  setDeclinedOptIns(initialState.declinedOptIns),
  setErrors(initialState.errors)
);

const cancelSubmission = pipe(
  setCompleted(false),
  setAcceptedOptIns(initialState.acceptedOptIns),
  setDeclinedOptIns(initialState.declinedOptIns),
  setFields(initialTermsStepState.fields),
  setErrors(initialState.errors)
);

const appDataLoaded = (data, state) =>
  setOptIns(getApplicationOptIns(data), state);

const termsReducer = (state = initialState, action) => {
  switch (action.type) {
    case termsActions.ACCEPT_OPT_IN:
      return acceptOptIn(action.id, state);

    case termsActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(
        validateTerms(
          getTermsOptIns(state),
          getAcceptedOptIns(state),
          getDeclinedOptIns(state)
        ),
        state
      );

    case termsActions.DECLINE_OPT_IN:
      return declineOptIn(action.id, state);

    case termsActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case termsActions.SET_TERMS_ACCEPTED:
      return setField(["termsAccepted"], action.accepted, state);

    case termsActions.SET_OPT_IN_TERMS_LOADING:
      return setOptInTermsDialogLoading(action.loading, state);

    case termsActions.SET_IFRAME_LOADING:
      return setIframeLoading(action.loading, state);

    case termsActions.UNDO_OPT_IN:
      return undoOptIn(action.id, state);

    case termsActions.VIEW_OPT_IN_TERMS:
      return viewOptInTerms(action.id, state);

    case submissionActions.ADD_ANOTHER_WORK:
      return addAnotherWork(state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case applicationActions.APP_DATA_LOADED:
      return appDataLoaded(action.data, state);

    case submissionActions.SET_ACTIVE_STEP:
      return action.step === steps.TERMS ? setCompleted(false)(state) : state;

    case submissionActions.INVALID_SUBMISSION:
    case submissionActions.SUBMISSION_ERROR:
      return setCompleted(false)(state);

    default:
      return state;
  }
};

export default termsReducer;
